import { NotificationService, type SnackbarVariants, useIntl, useLogger } from '@capsicum/common';
import { Snackbar } from '@capsicum/common-web';
import {
	useNonCustodialBalance,
	type WalletConnectErrors,
	type WalletConnectEventHandler,
	WalletConnectEvents
} from '@socios-blk/core';
import React, { useMemo } from 'react';

import { WALLET_CONNECT_ERRORS_MAP, WALLET_CONNECT_EVENTS_MAP } from './constants';

export const useWalletConnectEventHandler = () => {
	const { $t } = useIntl();
	const logger = useLogger();
	const { refetch: refetchNonCustodialBalance } = useNonCustodialBalance({}, { enabled: false });

	const openSnackbar = (variant: SnackbarVariants, title: string, description: string) => {
		NotificationService.addNotification({
			component: (
				<Snackbar
					description={description}
					testID="ncWallet-wallet-connect-snackbar"
					title={title}
					variant={variant}
				/>
			)
		});
	};

	const eventHandler = useMemo<WalletConnectEventHandler>(
		() => ({
			async onDeeplinkRedirection(url: string): Promise<void> {
				// do nothing on web for now
			},
			onError(code: WalletConnectErrors, error?: Error | string) {
				if (error) logger.error(error);

				const errorData = WALLET_CONNECT_ERRORS_MAP[code] ?? WALLET_CONNECT_ERRORS_MAP['PAIRING_ERROR'];
				const description = errorData.description ? $t(errorData.description) : '';

				openSnackbar('error', $t(errorData.title), description);
			},
			onNotification(code): void {
				const successData = WALLET_CONNECT_EVENTS_MAP[code];

				switch (code) {
					case WalletConnectEvents.TRANSACTION_SUCCESS:
						refetchNonCustodialBalance();
						break;
					default:
						break;
				}

				if (successData)
					openSnackbar(
						'success',
						$t(WALLET_CONNECT_EVENTS_MAP[code].title),
						$t(WALLET_CONNECT_EVENTS_MAP[code].description)
					);
			}
		}),
		[$t, logger, openSnackbar]
	);

	return {
		eventHandler
	};
};
